export type Options = {
  /** Maximum number of entries. Oldest entry is evicted when exceeded. */
  readonly maxSize: number;
  /** Time-to-live in milliseconds. Entries older than this are considered expired. Default: no expiry. */
  readonly maxAge?: number;
};

type CacheItem<ValueType> = {
  value: ValueType;
  expiry: number;
};

/**
 * Uses Map insertion-order as LRU order: `get` and `set` move the entry to the
 * most-recently-used tail; the Map's first entry is always the least-recently-used
 * and is the eviction candidate.
 */
export class LRU<KeyType, ValueType> {
  private readonly _cache = new Map<KeyType, CacheItem<ValueType>>();
  private readonly maxSize: number;
  private readonly maxAge: number;

  constructor(options: Options) {
    this.maxSize = options.maxSize;
    this.maxAge = options.maxAge ?? Infinity;
  }

  get size(): number {
    return this._cache.size;
  }

  set(key: KeyType, value: ValueType): this {
    // Delete first so re-insertion moves the key to the most-recently-used tail.
    this._cache.delete(key);
    this._cache.set(key, {value, expiry: Date.now() + this.maxAge});

    if (this._cache.size > this.maxSize) {
      const lruKey = this._cache.keys().next().value as KeyType;
      this._cache.delete(lruKey);
    }

    return this;
  }

  get(key: KeyType): ValueType | undefined {
    const item = this._cache.get(key);
    if (!item) return undefined;

    if (Date.now() > item.expiry) {
      this._cache.delete(key);
      return undefined;
    }

    // LRU touch: move to recently-used tail.
    this._cache.delete(key);
    this._cache.set(key, item);

    return item.value;
  }

  has(key: KeyType): boolean {
    const item = this._cache.get(key);
    if (!item) return false;

    if (Date.now() > item.expiry) {
      this._cache.delete(key);
      return false;
    }

    return true;
  }

  delete(key: KeyType): boolean {
    return this._cache.delete(key);
  }

  clear(): void {
    this._cache.clear();
  }
}

export default LRU;
